import { useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';

interface UseMapSetupProps {
  container: React.RefObject<HTMLDivElement>;
  center: { lat: number; lng: number };
  initialBounds?: [[number, number], [number, number]] | null;
}

// Use a global flag to prevent multiple simultaneous initialization attempts
let isInitializing = false;
let mapInstance: (maplibregl.Map & { originalStyle?: any }) | null = null;

export const useMapSetup = ({ container, center, initialBounds }: UseMapSetupProps) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);

  useEffect(() => {
    // Map setup check
    if (!container.current || mapInstance || isInitializing) return;

    // Set flag to prevent multiple initialization attempts
    isInitializing = true;

    // Add a small delay to ensure DOM is ready and avoid React batching issues
    const initTimer = setTimeout(() => {
      if (!container.current || mapInstance) {
        isInitializing = false;
        return;
      }

      // Creating map instance

      try {
        // OpenStreetMap style to avoid API limits
        const osmStyle: any = {
        version: 8,
        sources: {
          'osm-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
            ],
            tileSize: 256,
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
        },
        layers: [{
          id: 'osm-tiles-layer',
          type: 'raster',
          source: 'osm-tiles',
          minzoom: 0,
          maxzoom: 19
        }]
      };

      const baseMapConfig = {
        container: container.current,
        style: osmStyle,
        pitch: 0,
        bearing: 0,
        minZoom: 5,
        maxZoom: 20,
        maxPitch: 85,
        antialias: false,
        refreshExpiredTiles: false,
        fadeDuration: 0,
        maxTileCacheSize: 200,
        trackResize: true,
        renderWorldCopies: false,
        preserveDrawingBuffer: false,
        failIfMajorPerformanceCaveat: false,
        boxZoom: false,
        doubleClickZoom: true,
        dragRotate: true,  // Enable rotation with right-click drag
        pitchWithRotate: true,  // Enable pitch adjustment with rotation
        collectResourceTiming: false
      };

      // Create map with either bounds or center
      if (initialBounds) {
        // Using initial bounds
        mapInstance = new maplibregl.Map({
          ...baseMapConfig,
          bounds: initialBounds,
          fitBoundsOptions: {
            padding: { top: 100, bottom: 100, left: 400, right: 100 },
            maxZoom: 15
          }
        });
      } else {
        // Using center point
        mapInstance = new maplibregl.Map({
          ...baseMapConfig,
          center: [center.lng, center.lat],
          zoom: 8
        });
      }

      // Map instance created

      // Track map idle state
      let mapIdleTimer: NodeJS.Timeout;

      mapInstance.on('movestart', () => {
        clearTimeout(mapIdleTimer);
      });

      mapInstance.on('moveend', () => {
        mapIdleTimer = setTimeout(() => {
          // Map is idle
        }, 2000);
      });

      // Use idle event which fires when map is fully loaded and rendered
      let idleHandled = false;
      mapInstance.once('idle', () => {
        if (!idleHandled) {
          idleHandled = true;
          setMapLoaded(true);
          setStyleLoaded(true);
          isInitializing = false; // Reset flag once map is ready
          // Map fully loaded
        }
      });

      // Also listen for load event as backup
      mapInstance.on('load', () => {
        // Map load event
        setMapLoaded(true);

        // Add atmospheric lighting
        mapInstance!.setLight({
          anchor: 'viewport',
          color: 'white',
          intensity: 0.5
        });
      });

      // Add navigation controls with pitch visualization
      mapInstance.addControl(new maplibregl.NavigationControl({
        showCompass: true,
        visualizePitch: true
      }), 'top-right');
      mapInstance.addControl(new maplibregl.ScaleControl({
        maxWidth: 200,
        unit: 'metric'
      }), 'bottom-left');
      } catch (error) {
        console.error('Failed to create map:', error);
        isInitializing = false; // Reset flag on error
      }
    }, 100); // 100ms delay to ensure DOM is ready

    return () => {
      clearTimeout(initTimer);
      isInitializing = false; // Reset flag on cleanup
      if (mapInstance) {
        mapInstance.remove();
        mapInstance = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  return {
    map: mapInstance,
    mapLoaded,
    styleLoaded
  };
};